import { overflow } from "@prismicio/editor-support/String"
import {
  Box,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  FormField,
  type FormInputProps,
  Text,
} from "@prismicio/editor-ui"
import { useState } from "react"

import { useRequestRepositoryTags } from "../../../hooks/useRequestRepositoryTags"
import { useAPIExplorer } from "../../APIExplorerContext"
import { RequestFormInputBase } from "./RequestFormInputBase"
import { FormMultiInput } from "./RequestFormMultiInput"

/**
 * Display a fieldset managing a request tags settings.
 */
export function RequestTagsFieldset() {
  const { request } = useAPIExplorer()

  function setTags(tags: string[]) {
    request.setRequestOptions(request.type, {
      ...request.options[request.type],
      tags,
    })
  }

  return (
    <RequestFormInputBase label="TAGS *">
      <TagsFieldset
        tags={request.options[request.type]?.tags ?? []}
        setTags={setTags}
        overflowWidth={32}
        size="medium"
      />
    </RequestFormInputBase>
  )
}

interface TagsFieldsetProps
  extends Omit<FormInputProps, "value" | "onValueChange" | "placeholder"> {
  tags: string[]
  setTags: (tags: string[]) => void
  overflowWidth: number
}

/**
 * Display a fieldset managing tags.
 */
export function TagsFieldset(props: TagsFieldsetProps) {
  const { tags, setTags, overflowWidth, size, ...restProps } = props

  const repositoryTags = useRequestRepositoryTags()

  // We control the open state manually so that the input stays open when the user selects tags.
  const [isOpen, setIsOpen] = useState(false)

  if (!repositoryTags || !repositoryTags.length) {
    return (
      <FormMultiInput
        value={tags}
        onValueChange={setTags}
        size={size}
        {...restProps}
        placeholder="design, development, marketing"
      />
    )
  }

  function handleTagCheckedChange(tag: string, checked: boolean) {
    if (checked) {
      // Add tag if not already selected
      if (!tags.includes(tag)) {
        setTags([...tags, tag])
      }
    } else {
      // Remove tag if currently selected
      setTags(tags.filter((t) => t !== tag))
    }
  }

  return (
    <FormField {...restProps}>
      <ComboBox
        variant="detached-connected"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <ComboBoxInput
          onFocus={() => {
            setIsOpen(true)
            return true
          }}
          value={overflow(tags.join(", "), overflowWidth)}
          onValueChange={() => {}}
          placeholder="design, development, marketing"
          startAdornment={false}
          size={size}
          error={!tags.length}
        />
        <ComboBoxContent>
          {tags
            .filter((tag) => !repositoryTags.includes(tag))
            .concat(repositoryTags)
            .map((tag) => (
              <ComboBoxItem
                key={tag}
                value={tag}
                checked={tags.includes(tag)}
                onCheckedChange={(checked) =>
                  handleTagCheckedChange(tag, checked)
                }
                iconStyle={{ size: "small" }}
              >
                <Box padding={{ inline: 8, block: 4 }} alignItems="center">
                  {!repositoryTags.includes(tag) ? (
                    <Text color="tomato11">
                      {overflow(tag, overflowWidth)} (not in repository)
                    </Text>
                  ) : (
                    <Text>{overflow(tag, overflowWidth)}</Text>
                  )}
                </Box>
              </ComboBoxItem>
            ))}
        </ComboBoxContent>
      </ComboBox>
    </FormField>
  )
}
